import { css } from '@emotion/react';
import Tooltip from '@tippyjs/react';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';

import { CategoryOption } from 'api/api';

import { ShippingLogo, useLogoMap } from '~src/commons/components/logo/shipping-logo';

import { ShippingOptionLogoWrapper } from '../logo/shipping-option-logo';
import { Text } from '../text';

type OwnProps = {
  shippingMethods: string[];
  selectedOption?: CategoryOption;
};

export const LogoDeck: React.FunctionComponent<OwnProps> = ({
  shippingMethods,
  selectedOption,
}) => {
  const { logoMap } = useLogoMap();

  const uniqShippingMethods = uniqBy(shippingMethods, (shippingMethod: string) => {
    if (shippingMethod.startsWith('ics')) {
      return shippingMethod.slice(0, 7); // first 7 chars identify merchant, e.g. "ics-clr" means Caliroots for "ics-clrg" method
    }
    return shippingMethod.split('-')[0];
  });

  /**
   * Put selected shipping method at the start of logo deck
   */

  const currentOptionIndex = uniqShippingMethods.findIndex(
    el => el === selectedOption?.shipping_method
  );

  if (currentOptionIndex > 0) {
    const shippingMethodToMove = uniqShippingMethods[currentOptionIndex];

    uniqShippingMethods.splice(currentOptionIndex, 1);
    uniqShippingMethods.unshift(shippingMethodToMove);
  }

  const ref = React.useRef(null);

  const tooltipStr = uniqShippingMethods
    .filter(shippingMethod => shippingMethod !== 'unknown')
    .map(shippingMethod => logoMap(shippingMethod)?.name)
    .join(' / ');

  const tooltipContent = <Text color="white">{tooltipStr}</Text>;

  /*
   * Note: this element is purely for presentation and should have no focus.
   */
  return (
    <>
      <div css={styles.logoStackWrapperStyle} ref={ref}>
        <LogoStack shippingMethods={uniqShippingMethods} />
      </div>
      <Tooltip
        disabled={uniqShippingMethods.length <= 0 || tooltipStr === ''}
        content={tooltipContent}
        trigger="mouseenter focus click"
        arrow={false}
        reference={ref}
      />
    </>
  );
};

const LogoStack: React.FC<OwnProps> = ({ shippingMethods }) => {
  if (shippingMethods.length === 1) {
    return <ShippingLogo shippingMethod={shippingMethods[0]} size={30} />;
  }

  /*
   * The logo has a white border of 2 pixels.
   */
  return (
    <>
      <div css={styles.firstLogoWrapperStyle}>
        <ShippingOptionLogoWrapper size={28}>
          <ShippingLogo shippingMethod={shippingMethods[0]} size={24} />
        </ShippingOptionLogoWrapper>
      </div>
      <div css={styles.secondLogoWrapperStyle}>
        <ShippingLogo shippingMethod={shippingMethods[1]} size={24} />
      </div>
    </>
  );
};

const styles = {
  logoStackWrapperStyle: css({
    height: '100%',
    outline: 'none',
    border: '1px solid rgba(0,0,0,0)',
  }),
  firstLogoWrapperStyle: css({
    position: 'absolute',
    bottom: '-2px',
    left: '-2px',
    zIndex: 10,
  }),
  secondLogoWrapperStyle: css({
    position: 'absolute',
    top: 0,
    right: 0,
    zIndex: 5,
  }),
};
